import React, { useEffect, useState } from 'react';
import { Activity, Database, Zap, AlertCircle, TrendingUp, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { getPlanById } from '@/config/pricing';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { cn } from '@/lib/utils';

interface UsageData {
  requests: {
    used: number;
    limit: number | 'unlimited';
  };
  dbConnections: {
    used: number;
    limit: number | 'unlimited';
  };
  tokens: {
    used: number;
    limit: number | null;
  };
  period: {
    start: Date;
    end: Date;
  };
}

interface UsageTrackerProps {
  onUpgrade?: () => void;
  compact?: boolean;
}

export const UsageTracker: React.FC<UsageTrackerProps> = ({
  onUpgrade,
  compact = false,
}) => {
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();
  const { subscription } = useSubscription();
  const currentPlanId = subscription?.plan_id || 'free';
  const currentPlan = getPlanById(currentPlanId);

  useEffect(() => {
    // Only fetch usage data if user is authenticated
    if (isAuthenticated) {
      fetchUsageData();
    } else {
      setLoading(false);
      setError('Please log in to view your usage data');
    }
  }, [isAuthenticated]);

  const fetchUsageData = async () => {
    try {
      setError(null); // Clear any previous errors

      // Import API service dynamically to avoid circular dependencies
      const { default: apiService } = await import('@/services/api');

      // Fetch real usage data from API
      const usageResponse = await apiService.getCurrentUsage();

      const realUsage: UsageData = {
        requests: {
          used: usageResponse.requests_used,
          limit: usageResponse.requests_limit === null ? 'unlimited' : usageResponse.requests_limit,
        },
        dbConnections: {
          used: usageResponse.connections_used,
          limit: usageResponse.connections_limit === null ? 'unlimited' : usageResponse.connections_limit,
        },
        tokens: {
          used: usageResponse.tokens_used,
          limit: usageResponse.tokens_limit,
        },
        period: {
          start: new Date(usageResponse.period_start),
          end: new Date(usageResponse.period_end),
        },
      };

      console.log('✅ Real usage data loaded:', realUsage);
      setUsage(realUsage);
    } catch (error: any) {
      console.error('Failed to fetch usage data:', error);

      // Handle specific authentication errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setError('Authentication required. Please log in to view usage data.');
      } else if (error?.response?.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else {
        // Fallback to basic data based on current plan if API fails
        console.warn('Using fallback usage data due to API error');
        const fallbackUsage: UsageData = {
          requests: {
            used: 0,
            limit: currentPlan?.limits.requests || 20,
          },
          dbConnections: {
            used: 0,
            limit: currentPlan?.limits.dbConnections || 3,
          },
          tokens: {
            used: 0,
            limit: currentPlan?.limits.tokens || null,
          },
          period: {
            start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
          },
        };
        setUsage(fallbackUsage);
        setError('Unable to load live usage data. Showing plan defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (used: number, limit: number | 'unlimited'): number => {
    if (limit === 'unlimited') return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-600';
    if (percentage >= 75) return 'bg-yellow-600';
    return 'bg-green-600';
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const daysRemaining = usage ? Math.ceil((usage.period.end.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="h-4 bg-muted animate-pulse rounded" />
            <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
            <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state if there's an authentication or API error
  if (error && !usage) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to Load Usage Data</AlertTitle>
            <AlertDescription>
              {error}
              <Button
                variant="outline"
                size="sm"
                className="ml-4"
                onClick={() => {
                  if (isAuthenticated) {
                    fetchUsageData();
                  } else {
                    window.location.href = '/login';
                  }
                }}
              >
                {isAuthenticated ? 'Retry' : 'Login'}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!usage || !currentPlan) return null;

  const requestPercentage = calculatePercentage(usage.requests.used, usage.requests.limit);
  const dbPercentage = calculatePercentage(usage.dbConnections.used, usage.dbConnections.limit);
  const tokenPercentage = usage.tokens.limit ? calculatePercentage(usage.tokens.used, usage.tokens.limit) : 0;

  const isApproachingLimit = requestPercentage >= 75 || dbPercentage >= 75 || tokenPercentage >= 75;

  if (compact) {
    return (
      <div className="space-y-3">
        {/* Request Usage */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Requests</span>
            <span className={cn("text-sm", getUsageColor(requestPercentage))}>
              {usage.requests.limit === 'unlimited' ? (
                formatNumber(usage.requests.used)
              ) : (
                `${usage.requests.used} / ${usage.requests.limit}`
              )}
            </span>
          </div>
          {usage.requests.limit !== 'unlimited' && (
            <Progress value={requestPercentage} className="h-2" />
          )}
        </div>

        {/* Token Usage */}
        {usage.tokens.limit && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">Tokens</span>
              <span className={cn("text-sm", getUsageColor(tokenPercentage))}>
                {formatNumber(usage.tokens.used)} / {formatNumber(usage.tokens.limit)}
              </span>
            </div>
            <Progress value={tokenPercentage} className="h-2" />
          </div>
        )}

        {isApproachingLimit && (
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={onUpgrade}
          >
            Upgrade Plan
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Subscription Status Alert */}
      {subscription?.status === 'active' && subscription?.plan_id !== 'free' && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">Active Subscription</AlertTitle>
          <AlertDescription className="text-green-800">
            Your {currentPlan?.name} plan is active. Renews on {new Date(subscription.current_period_end).toLocaleDateString()}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Usage Overview</CardTitle>
              <CardDescription>
                {currentPlan.name} Plan • {daysRemaining} days remaining
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-xs">
              Resets {usage.period.end.toLocaleDateString()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Requests */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">API Requests</span>
              </div>
              <span className={cn("text-sm font-bold", getUsageColor(requestPercentage))}>
                {usage.requests.limit === 'unlimited' ? (
                  <>
                    {formatNumber(usage.requests.used)} <span className="text-muted-foreground">used</span>
                  </>
                ) : (
                  <>
                    {usage.requests.used} / {usage.requests.limit}
                    <span className="text-muted-foreground ml-1">({requestPercentage.toFixed(0)}%)</span>
                  </>
                )}
              </span>
            </div>
            {usage.requests.limit !== 'unlimited' && (
              <Progress
                value={requestPercentage}
                className={cn("h-3", getProgressColor(requestPercentage))}
              />
            )}
          </div>

          {/* Database Connections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Database Connections</span>
              </div>
              <span className={cn("text-sm font-bold", getUsageColor(dbPercentage))}>
                {usage.dbConnections.limit === 'unlimited' ? (
                  <>
                    {usage.dbConnections.used} <span className="text-muted-foreground">active</span>
                  </>
                ) : (
                  <>
                    {usage.dbConnections.used} / {usage.dbConnections.limit}
                    <span className="text-muted-foreground ml-1">({dbPercentage.toFixed(0)}%)</span>
                  </>
                )}
              </span>
            </div>
            {usage.dbConnections.limit !== 'unlimited' && (
              <Progress
                value={dbPercentage}
                className={cn("h-3", getProgressColor(dbPercentage))}
              />
            )}
          </div>

          {/* Tokens */}
          {usage.tokens.limit && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Token Usage</span>
                </div>
                <span className={cn("text-sm font-bold", getUsageColor(tokenPercentage))}>
                  {formatNumber(usage.tokens.used)} / {formatNumber(usage.tokens.limit)}
                  <span className="text-muted-foreground ml-1">({tokenPercentage.toFixed(0)}%)</span>
                </span>
              </div>
              <Progress
                value={tokenPercentage}
                className={cn("h-3", getProgressColor(tokenPercentage))}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warning Alert */}
      {isApproachingLimit && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Approaching Usage Limits</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>You're approaching your plan limits. Upgrade to continue uninterrupted.</span>
            <Button size="sm" onClick={onUpgrade} className="ml-4">
              <TrendingUp className="h-4 w-4 mr-1" />
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* API Error Warning */}
      {error && usage && (
        <Alert variant="default">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Notice</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUsageData}
              className="ml-4"
            >
              Refresh
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};